import { PluginKey, Plugin } from '@tiptap/pm/state';
import { ySyncPluginKey, ProsemirrorBinding, absolutePositionToRelativePosition, relativePositionToAbsolutePosition } from 'y-prosemirror';
import * as Y from 'yjs';
import { Mapping } from '../../../../node_modules/prosemirror-transform/dist/index.js';
import { Extension } from '@tiptap/core';

class YJSMapping extends Mapping {
  constructor(state, tr) {
    const mapping = tr.mapping;
    const mirror = mapping.mirror;
    super(mapping.maps, mirror && mirror.slice(), mapping.from, mapping.to);
    this.state = state;
    this.tr = tr;
    const ySyncState = ySyncPluginKey.getState(state);
    if (!ySyncState) throw new Error("YJS is not initialized");
    const yjsMappingState = YJSMappingKey.getState(state);
    if (!yjsMappingState) throw new Error("YJS Mapping is not initialized");
    this.binding = ySyncState.binding;
    this.y = ySyncState.doc;
    this.wasYChanged = ySyncState.isChangeOrigin;
    this.previousBinding = yjsMappingState.previousProsemirrorBinding?.previousBinding ?? null;
  }
  map(pos, assoc) {
    if (!this.wasYChanged || !this.previousBinding) return super.map(pos, assoc);
    const relativePosition = absolutePositionToRelativePosition(pos, this.previousBinding.type, this.previousBinding.mapping);
    const newPos = relativePositionToAbsolutePosition(this.y, this.binding.type, relativePosition, this.binding.mapping) ?? 0;
    return newPos;
  }
  mapResult(pos, assoc) {
    if (!this.wasYChanged) return super.mapResult(pos, assoc);
    const absolutePosition = this.map(pos, assoc);
    return {
      pos: absolutePosition,
      deleted: false,
      deletedBefore: false,
      deletedAfter: false,
      deletedAcross: false
    };
  }
}
class PreviousProsemirrorBinding {
  constructor(doc) {
    this.previousBinding = null;
    this.editorView = null;
    this.doc = doc;
    this.beforeAllTransactions = () => {
      if (!this.editorView || this.previousBinding) return;
      const ySyncState = ySyncPluginKey.getState(this.editorView?.state);
      if (!ySyncState) return;
      const binding = ySyncState.binding;
      const newDoc = new Y.Doc();
      Y.applyUpdate(newDoc, Y.encodeStateAsUpdate(this.doc));
      const name = Y.findRootTypeKey(binding.type);
      const newType = newDoc.getXmlFragment(name);
      const mapping = /* @__PURE__ */ new Map();
      const entries = Array.from(binding.mapping.values());
      let index = 0;
      const updateMap = (t) => {
        if (t instanceof Y.XmlElement) {
          t.forEach((child) => {
            updateMap(child);
          });
          mapping.set(t, entries[index]);
          index++;
        }
      };
      newType.toArray().forEach((t) => {
        updateMap(t);
      });
      const newBinding = new ProsemirrorBinding(newType, mapping);
      this.previousBinding = newBinding;
    };
    this.afterAllTransactions = () => {
      this.previousBinding = null;
    };
  }
  initView(editorView) {
    this.editorView = editorView;
    this.doc.on("beforeAllTransactions", this.beforeAllTransactions);
    this.doc.on("afterAllTransactions", this.afterAllTransactions);
  }
  destroy() {
    this.editorView = null;
    this.previousBinding = null;
    this.doc.off("beforeAllTransactions", this.beforeAllTransactions);
    this.doc.off("afterAllTransactions", this.afterAllTransactions);
  }
}
const YJSMappingKey = new PluginKey("yjs-mapping");
const YJSMappingPlugin = () => {
  return new Plugin({
    key: YJSMappingKey,
    view(editorView) {
      const yjsMappingState = YJSMappingKey.getState(editorView.state);
      if (!yjsMappingState) return {};
      yjsMappingState.previousProsemirrorBinding?.initView(editorView);
      return {
        destroy() {
          yjsMappingState.previousProsemirrorBinding?.destroy();
        }
      };
    },
    state: {
      init() {
        return {
          previousProsemirrorBinding: null
        };
      },
      apply(_, state, __, editorState) {
        if (state.previousProsemirrorBinding) return state;
        const ySyncState = ySyncPluginKey.getState(editorState);
        if (!ySyncState) return state;
        return {
          previousProsemirrorBinding: new PreviousProsemirrorBinding(ySyncState.doc)
        };
      }
    }
  });
};
const YJSMappingExtension = Extension.create({
  name: "yjs-mapping",
  priority: 999,
  addProseMirrorPlugins() {
    return [
      YJSMappingPlugin()
    ];
  }
});

export { PreviousProsemirrorBinding, YJSMapping, YJSMappingExtension };
//# sourceMappingURL=YJSMappingPlugin.js.map
